import {
  ReferenceImageMatch,
  SampleOosPrediction,
} from "@/dev/sample-out-of-stock-predictions";
import {
  isUnconfirmedSuggestion,
  useCoverageBetaStore,
  useIsEmptyAreaActive,
} from "@/store/useCoverageBetaStore";
import { useBoxReshape } from "@/hooks/useBoxReshape";
import { focusRefImgForArea } from "@/utils/coverage-beta-focus";
import { getDetectionBoxGeometry } from "@/utils/detection-box-geometry";
import {
  nearestProductIds,
  type ProductSpot,
} from "@/utils/nearest-product-ids";
import { cn } from "@/utils/utils";
import { produce } from "immer";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { BoxCornerHandles } from "./BoxCornerHandles";
import { OrientedBoxOverlay } from "./OrientedBoxOverlay";
import "./QueryEmptyAreaDetectionBox.css";

interface Props {
  emptyAreaDetection: any;
  emptyAreaIdx: number;
  hasPrediction?: boolean;
  referenceMatches: ReferenceImageMatch[];
  zoomToElement: Function;
  out_of_stock_predictions: SampleOosPrediction[];
  /** What hover localizes against — see `neighbourSpots`. */
  neighbourSpots: ProductSpot[];
  /** Read-only view (a similar view): no move/resize, hover/click only. */
  isViewOnly?: boolean;
}

/** How many neighbouring product spots to highlight on hover. */
const NEIGHBOUR_COUNT = 5;

/**
 * Class on a box that can be moved/resized; the query view's pan ignores
 * pointer-downs on it, so dragging the box doesn't also drag the image.
 */
export const RESHAPABLE_BOX_CLASS = "coverage-beta-reshapable-box";

export const QueryEmptyAreaDetectionBox = (props: Props) => {
  const {
    emptyAreaDetection,
    zoomToElement,
    emptyAreaIdx,
    referenceMatches,
    out_of_stock_predictions,
    neighbourSpots,
    isViewOnly,
  } = props;

  const points = emptyAreaDetection?.points ?? [];
  const setNeighbourProductIds = useCoverageBetaStore(
    (s) => s.setNeighbourProductIds,
  );

  // On hover, highlight this area's own picks plus the products nearest it (in
  // both the reference image and the planogram) so the region is easy to spot.
  const highlightNeighbours = () => {
    const area = useCoverageBetaStore.getState().idxToFeEmptyArea[emptyAreaIdx];
    const ownIds = (area?.selectedProducts ?? []).flatMap((p) =>
      p.id ? [p.id] : [],
    );
    const nearIds = nearestProductIds(points, neighbourSpots, NEIGHBOUR_COUNT);
    setNeighbourProductIds([...new Set([...ownIds, ...nearIds])]);
  };

  const isDrawMode = useCoverageBetaStore(
    (s) => s.activeMode === "draw-box" || s.activeMode === "draw-person",
  );
  const isSourceHuman = useCoverageBetaStore(
    (s) => s.idxToFeEmptyArea[emptyAreaIdx].source === "human",
  );
  const isFromSam2 = useCoverageBetaStore(
    (s) => s.idxToFeEmptyArea[emptyAreaIdx].propagated != null,
  );
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  // Move/resize, on a view that allows labeling: a human box in draw mode, or
  // a box SAM 2 pre-filled at any time — a guess that's a little off is fixed
  // in place, without switching modes.
  const canReshape =
    !isViewOnly && !isReadOnly && ((isDrawMode && isSourceHuman) || isFromSam2);

  // Move/resize (see canReshape), keeping a tilted box's angle. The box draws
  // at `livePoints` while dragging; the store is written once, on release.
  const { boxRef, livePoints, grab, consumeDragClick } = useBoxReshape({
    points,
    enabled: canReshape,
    onCommit: (next) => {
      const { idxToFeEmptyArea, setIdxToFeEmptyArea } =
        useCoverageBetaStore.getState();
      setIdxToFeEmptyArea(
        produce(idxToFeEmptyArea, (draft) => {
          if (draft[emptyAreaIdx]) draft[emptyAreaIdx].points = next;
        }),
      );
    },
  });
  const shownPoints = livePoints ?? points;
  // The div is the hit area, on the box's bounding box; the overlay strokes the
  // shape, oriented (rotated) when the model detected it that way.
  const { style: boundingBoxStyle, corners } = useMemo(
    () => getDetectionBoxGeometry(shownPoints),
    [shownPoints],
  );

  const hasPrediction = !isEmpty(referenceMatches);
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const setActiveArea = useCoverageBetaStore((s) => s.setActiveArea);
  const isActiveEmptyArea = useIsEmptyAreaActive(emptyAreaIdx);
  const showQaPulse = isQaMode && isActiveEmptyArea;
  const areProductsSelectedForThisEmptyArea = useCoverageBetaStore((s) => {
    return s.idxToFeEmptyArea[emptyAreaIdx].selectedProducts.length > 0;
  });

  const isDeleted = useCoverageBetaStore((s) => {
    return s.idxToFeEmptyArea[emptyAreaIdx].isDeleted;
  });

  const isHidden = useCoverageBetaStore((s) => {
    return s.idxToFeEmptyArea[emptyAreaIdx].isHidden;
  });

  const isValid = useCoverageBetaStore((s) => {
    return s.idxToFeEmptyArea[emptyAreaIdx].isValid;
  });

  // Reviewed with no matching product ("no match").
  const isNoMatch = isValid && !areProductsSelectedForThisEmptyArea;

  // A SAM 2 suggestion not yet confirmed: violet, never the green of a
  // labeler's decision, until it's submitted or its products are changed.
  const isSuggestion = useCoverageBetaStore((s) =>
    isUnconfirmedSuggestion(s.idxToFeEmptyArea[emptyAreaIdx]),
  );

  // Outside QA mode a deleted area renders nothing (it's just not part of
  // the task anymore). In QA mode it needs to be visible and inspectable —
  // reviewing a deletion means looking at what was deleted — so it renders
  // with a neutral, deletion-specific style below instead of returning null.
  if (isDeleted && !isQaMode) return null;

  // Stroke and fill per state. `group-hover:` picks up the wrapper's `group`.
  // A deleted box (only reachable here in QA mode) always gets the neutral
  // gray-dashed treatment — product-state colors would suggest it's still an
  // active, undecided box.
  let quadStyle;
  if (isDeleted) {
    quadStyle = {
      className: cn(
        "stroke-gray-400 group-hover:fill-gray-400/40",
        isActiveEmptyArea ? "fill-gray-400/40" : "fill-gray-400/20",
      ),
      strokeWidth: 2,
      dashed: true,
    };
  } else if (isSuggestion) {
    quadStyle = {
      className: cn(
        "stroke-violet-400/70 group-hover:fill-violet-400/60",
        isActiveEmptyArea ? "fill-violet-400/60" : "fill-violet-400/40",
      ),
      strokeWidth: 4,
      dashed: false,
    };
  } else if (areProductsSelectedForThisEmptyArea) {
    quadStyle = {
      className:
        "stroke-green-400/50 fill-green-400/50 group-hover:fill-green-400/70",
      strokeWidth: 4,
      dashed: false,
    };
  } else if (isNoMatch) {
    quadStyle = {
      className: cn(
        "stroke-gray-500 group-hover:fill-gray-500/70",
        isActiveEmptyArea ? "fill-gray-500/70" : "fill-gray-500/50",
      ),
      strokeWidth: 2,
      dashed: false,
    };
  } else if (hasPrediction) {
    quadStyle = {
      className: cn(
        "stroke-blue-400 group-hover:fill-blue-400/70",
        isActiveEmptyArea && "fill-blue-400/50",
      ),
      strokeWidth: 2,
      dashed: true,
    };
  } else {
    quadStyle = {
      className: cn(
        "stroke-orange-400 group-hover:fill-orange-400/70",
        isActiveEmptyArea && "fill-orange-500/50",
      ),
      strokeWidth: 2,
      dashed: true,
    };
  }

  return (
    <div
      ref={boxRef}
      data-empty-area-index={emptyAreaIdx}
      {...grab("move")}
      onMouseEnter={highlightNeighbours}
      onMouseLeave={() => setNeighbourProductIds([])}
      className={cn(
        "absolute cursor-pointer group",
        isHidden && "opacity-0",
        // A reshapable box sits above draw mode's crosshair overlay (z-50) so
        // it can capture move/resize pointer events.
        canReshape && cn("z-[55] cursor-move", RESHAPABLE_BOX_CLASS),
        showQaPulse && "qa-pulse",
      )}
      style={{ ...boundingBoxStyle }}
      // onClick={({ currentTarget }) => {
      onClick={() => {
        if (consumeDragClick()) return;
        const { confirming, nudgeConfirm } = useCoverageBetaStore.getState();
        // Don't switch the active area while a confirm step is open for another
        // area — that would let the pick land on the wrong area. Nudge the
        // confirm panel so the blocked click is explained, not silent.
        if (confirming != null && confirming.emptyAreaIdx !== emptyAreaIdx) {
          nudgeConfirm();
          return;
        }
        setActiveArea(emptyAreaIdx);
        // Refocus the query view on the clicked empty area (centred + zoomed;
        // scale is tunable). Skipped if the box isn't mounted.
        if (boxRef.current) zoomToElement(boxRef.current, 2.5, 300);

        focusRefImgForArea(emptyAreaIdx, out_of_stock_predictions);
      }}
    >
      <OrientedBoxOverlay corners={corners} {...quadStyle} />

      {canReshape && (
        <BoxCornerHandles
          points={shownPoints}
          grab={grab}
          className={isSuggestion ? "border-violet-500" : "border-orange-500"}
        />
      )}

      {/* How many shelf-image matches the model predicted for this area. */}
      {hasPrediction && (
        <div
          className="bg-blue-500 rounded-xl size-3.5 text-[10px] absolute -top-2 -right-2 text-white flex justify-center items-center"
          title={`${referenceMatches.length} shelf-image ${referenceMatches.length === 1 ? "match" : "matches"} predicted`}
        >
          {referenceMatches.length}
        </div>
      )}
      <div
        className="text-white font-bold absolute"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {emptyAreaIdx}
      </div>
    </div>
  );
};
